import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { getAccessToken } from '../utils/authSession';

function AdminGallery() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [formData, setFormData] = useState({
    image: '',
    category: 'Facilities',
    caption: ''
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const navigate = useNavigate();

  const categories = ['Facilities', 'Events', 'Outreach', 'Staff'];

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchImages();
  }, [navigate, categoryFilter, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const token = getAccessToken();
      const params = new URLSearchParams();
      if (categoryFilter) params.append('category', categoryFilter);
      params.append('page', String(currentPage));
      params.append('limit', '50');

      const response = await axios.get(`/api/gallery?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setImages(response.data.images);
      setPagination(response.data.pagination);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
      console.error('Error fetching gallery images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setImagePreview(null);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      e.target.value = '';
      setImagePreview(null);
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    setUploadingImage(true);
    try {
      const token = getAccessToken();
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const response = await axios.post('/api/upload', uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Use filename directly from response
      const filename = response.data.filename;
      
      if (!filename) {
        throw new Error('No filename returned from server');
      }
      
      setFormData(prev => ({ ...prev, image: filename }));
      e.target.value = '';
    } catch (error) {
      alert('Failed to upload image');
      console.error('Error uploading image:', error);
      setImagePreview(null);
      e.target.value = '';
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAdd = () => {
    setFormData({
      image: '',
      category: 'Facilities',
      caption: ''
    });
    setFormError('');
    setImagePreview(null);
    setUploadingImage(false);
    setShowAddModal(true);
  };

  const handleEdit = (img) => {
    setEditingImage(img);
    setFormData({
      image: img.image || '',
      category: img.category || 'Facilities',
      caption: img.caption || ''
    });
    setFormError('');
    setImagePreview(null);
    setUploadingImage(false);
    setShowEditModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    
    // Check if image is still uploading
    if (uploadingImage) {
      setFormError('Please wait for the image to finish uploading');
      return;
    }
    
    setFormLoading(true);

    if (!formData.image) {
      setFormError('Please select and upload an image file');
      setFormLoading(false);
      return;
    }
    
    if (!formData.category) {
      setFormError('Please select a category');
      setFormLoading(false);
      return;
    }
    
    try {
      const token = getAccessToken();
      const url = editingImage ? `/api/gallery/${editingImage.id}` : '/api/gallery';
      const method = editingImage ? 'put' : 'post';

      await axios[method](url, formData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setShowAddModal(false);
      setShowEditModal(false);
      setEditingImage(null);
      fetchImages();
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
      setFormError(error.response?.data?.error || 'Failed to save image');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      const token = getAccessToken();
      await axios.delete(`/api/gallery/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      fetchImages();
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
      alert('Failed to delete image');
    }
  };

  return (
    <AdminLayout>
      <div className="container-fluid p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Gallery Management</h2>
          <button className="btn btn-primary" onClick={handleAdd}>
            <i className="bi bi-plus-circle me-2"></i>Add New Image
          </button>
        </div>

        {/* Category Filter */}
        <div className="row mb-3">
          <div className="col-md-4">
            <select
              className="form-select"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Images Grid */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" role="status"></div>
          </div>
        ) : (
          <>
            <div className="row g-3">
              {images.map(img => (
                <div key={img.id} className="col-lg-3 col-md-4 col-sm-6">
                  <div className="card">
                  <div style={{ position: 'relative', paddingTop: '75%', overflow: 'hidden' }}>
                    <img
                      src={img.image ? (img.image.startsWith('/uploads/') ? img.image : `/uploads/${img.image}`) : '/img/Gallery/opd1.jpg'}
                      alt={img.caption || 'Gallery image'}
                      className="card-img-top"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.src = '/img/Gallery/opd1.jpg';
                      }}
                    />
                  </div>
                  <div className="card-body">
                    <span className="badge bg-primary mb-2">{img.category}</span>
                    <p className="card-text" style={{ fontSize: '0.9rem', minHeight: '40px' }}>
                      {img.caption || <em className="text-muted">No caption</em>}
                    </p>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-primary flex-fill" onClick={() => handleEdit(img)}>
                        <i className="bi bi-pencil"></i> Edit
                      </button>
                      <button className="btn btn-sm btn-danger flex-fill" onClick={() => handleDelete(img.id)}>
                        <i className="bi bi-trash"></i> Delete
                      </button>
                    </div>
                  </div>
                  </div>
                </div>
              ))}
            </div>
            {pagination.pages > 1 && (
              <div className="mt-3 d-flex justify-content-between align-items-center">
                <p className="text-muted mb-0">
                  Page {pagination.page} of {pagination.pages} (Total: {pagination.total})
                </p>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={loading || (pagination.page || 1) <= 1}
                  >
                    Previous
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    disabled={loading || (pagination.page || 1) >= (pagination.pages || 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Add Modal */}
        {showAddModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
            <div className="modal-dialog modal-dialog-scrollable" style={{ maxHeight: '90vh', margin: '2rem auto' }}>
              <div className="modal-content" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                <div className="modal-header" style={{ flexShrink: 0 }}>
                  <h5 className="modal-title">Add New Image</h5>
                  <button type="button" className="btn-close" onClick={() => {
                    setShowAddModal(false);
                    setImagePreview(null);
                  }}></button>
                </div>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                    {formError && <div className="alert alert-danger">{formError}</div>}
                    
                    <div className="mb-3">
                      <label className="form-label">Image *</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                      />
                      {uploadingImage && <div className="text-muted mt-2">Uploading...</div>}
                      {/* Show preview of selected file (before or after upload) */}
                      {imagePreview ? (
                        <div className="mt-2">
                          <p className="text-muted small mb-1">
                            {uploadingImage ? 'Uploading...' : 'Selected image:'}
                          </p>
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                          {formData.image && !uploadingImage && (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger mt-2"
                              onClick={() => {
                                setFormData({ ...formData, image: '' });
                                setImagePreview(null);
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ) : formData.image ? (
                        <div className="mt-2">
                          <p className="text-muted small mb-1">Current image:</p>
                          <img 
                            src={formData.image.startsWith('/uploads/') ? formData.image : `/uploads/${formData.image}`} 
                            alt="Preview" 
                            style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'cover', borderRadius: '4px' }}
                            onError={(e) => {
                              console.error('Error loading image:', formData.image);
                              e.target.src = '/img/Gallery/opd1.jpg';
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-danger mt-2"
                            onClick={() => {
                              setFormData({ ...formData, image: '' });
                              setImagePreview(null);
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ) : null}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Category *</label>
                      <select
                        className="form-select"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        required
                      >
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Caption</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={formData.caption}
                        onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                        placeholder="Optional image caption"
                      />
                    </div>
                  </div>
                  <div className="modal-footer" style={{ flexShrink: 0 }}>
                    <button type="button" className="btn btn-secondary" onClick={() => {
                      setShowAddModal(false);
                      setImagePreview(null);
                    }}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={formLoading || !formData.image}>
                      {formLoading ? 'Saving...' : 'Save Image'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
            <div className="modal-dialog modal-dialog-scrollable" style={{ maxHeight: '90vh', margin: '2rem auto' }}>
              <div className="modal-content" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                <div className="modal-header" style={{ flexShrink: 0 }}>
                  <h5 className="modal-title">Edit Image</h5>
                  <button type="button" className="btn-close" onClick={() => {
                    setShowEditModal(false);
                    setImagePreview(null);
                  }}></button>
                </div>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                    {formError && <div className="alert alert-danger">{formError}</div>}
                    
                    <div className="mb-3">
                      <label className="form-label">Current Image</label>
                      {formData.image && (
                        <div>
                          <img 
                            src={formData.image.startsWith('/uploads/') ? formData.image : `/uploads/${formData.image}`} 
                            alt="Current" 
                            style={{ maxWidth: '100%', maxHeight: '300px' }}
                            onError={(e) => {
                              e.target.src = '/img/Gallery/opd1.jpg';
                            }}
                          />
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Change Image</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                      />
                      {uploadingImage && <div className="text-muted mt-2">Uploading...</div>}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Category *</label>
                      <select
                        className="form-select"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        required
                      >
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Caption</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={formData.caption}
                        onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => {
                      setShowEditModal(false);
                      setImagePreview(null);
                    }}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={formLoading}>
                      {formLoading ? 'Saving...' : 'Update Image'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminGallery;
